import React, { useEffect, useState, useRef, useCallback } from 'react';
import './DesignTicker.css';
import { API_URL } from '../config';

const VALID_URL = (url) =>
  url &&
  !url.includes('picsum') &&
  (url.startsWith('https://fashionai-api') || url.startsWith('data:'));

const SKELETONS = Array.from({ length: 8 }, (_, i) => i);

export default function DesignTicker({ onSelectDesign }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  const fetchDesigns = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/designs?limit=50&sort=recent`);
      if (!res.ok) return;
      const data = await res.json();
      const valid = (data.designs || []).filter((d) => VALID_URL(d.image_url));
      if (valid.length >= 4) {
        setDesigns(valid.slice(0, 20));
      }
    } catch (err) {
      console.warn('[DesignTicker] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDesigns();
    // Refresh every 2 minutes
    const interval = setInterval(fetchDesigns, 120_000);
    return () => clearInterval(interval);
  }, [fetchDesigns]);

  if (!loading && designs.length < 4) return null;

  // Duplicate the list so the seamless loop looks natural
  const tiles = [...designs, ...designs];
  const isSlow = designs.length <= 8;

  return (
    <div className="design-ticker-wrapper" aria-label="Live design showcase">
      {loading ? (
        <div className="design-ticker-skeleton">
          {SKELETONS.map((i) => (
            <div key={i} className="design-ticker-skel-tile">
              <div className="design-ticker-skel-circle" />
              <div className="design-ticker-skel-text" style={{ width: `${80 + (i % 3) * 30}px` }} />
            </div>
          ))}
        </div>
      ) : (
        <div
          ref={trackRef}
          className={`design-ticker-track${isSlow ? ' slow' : ''}`}
        >
          {/* Live indicator as first "tile" */}
          <div className="design-ticker-live">
            <span className="design-ticker-live-dot" />
            <span className="design-ticker-live-text">Live</span>
          </div>

          {tiles.map((d, i) => (
            <React.Fragment key={`${d.id}-${i}`}>
              <button
                className="design-ticker-tile"
                onClick={() => onSelectDesign?.(d)}
                title={d.prompt}
                aria-label={`Load design: ${d.prompt}`}
              >
                <img
                  className="design-ticker-img"
                  src={d.image_url}
                  alt=""
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="design-ticker-prompt">
                  {d.prompt?.length > 40 ? d.prompt.slice(0, 40) + '…' : d.prompt}
                </span>
              </button>

              {/* Separator dot between tiles */}
              {i < tiles.length - 1 && <span className="design-ticker-sep" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
